import React, { useContext, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {useCartContext} from "./CartProvider.jsx";
import {showAlert} from "../HelperAlerts/HelpersAlerts.jsx";
import {getThemeContext} from "./ThemeProvider.jsx";

const authContext = React.createContext();
const authToggleContext = React.createContext();

const REGISTER_FIELD_LABELS = {
    name: 'Nombre',
    email: 'Email',
    password: 'Contraseña',
    password2: 'Confirmación de contraseña',
    username: 'Usuario',
    non_field_errors: 'Registro',
};

const REGISTER_ERROR_TRANSLATIONS = [
    [/this field is required/i, 'Este campo es obligatorio.'],
    [/this field may not be blank/i, 'Este campo no puede estar vacío.'],
    [/enter a valid email address/i, 'Introduce un email válido.'],
    [/user with this email already exists/i, 'Ya existe una cuenta con este email.'],
    [/email.*already exists/i, 'Ya existe una cuenta con este email.'],
    [/already exists/i, 'Este valor ya está en uso.'],
    [/a user with that username already exists/i, 'Ya existe una cuenta con este usuario.'],
    [/the password is too similar to the .* email/i, 'La contraseña se parece demasiado al email.'],
    [/the password is too similar to the .* username/i, 'La contraseña se parece demasiado al usuario.'],
    [/the password is too similar to the .* name/i, 'La contraseña se parece demasiado al nombre.'],
    [/this password is too short/i, 'La contraseña es demasiado corta.'],
    [/it must contain at least (\d+) characters/i, 'Debe tener al menos $1 caracteres.'],
    [/ensure this field has at least (\d+) characters/i, 'Debe tener al menos $1 caracteres.'],
    [/ensure this field has no more than (\d+) characters/i, 'Debe tener como máximo $1 caracteres.'],
    [/this password is too common/i, 'La contraseña es demasiado común.'],
    [/this password is entirely numeric/i, 'La contraseña no puede ser solo numérica.'],
    [/password fields didn't match/i, 'Las contraseñas no coinciden.'],
    [/unable to create account/i, 'No se pudo crear la cuenta.'],
    [/invalid/i, 'El valor introducido no es válido.'],
];

const translateRegisterMessage = (message) => {
    const text = String(message ?? '').trim();
    if (!text) return '';

    const translation = REGISTER_ERROR_TRANSLATIONS.find(([pattern]) => pattern.test(text));
    if (translation) return text.replace(translation[0], translation[1]);

    return text;
};

const collectRegisterMessages = (data, field = null) => {
    if (!data) return [];

    if (typeof data === 'string') {
        const message = translateRegisterMessage(data);
        return field && REGISTER_FIELD_LABELS[field]
            ? [`${REGISTER_FIELD_LABELS[field]}: ${message}`]
            : [message];
    }

    if (Array.isArray(data)) {
        return data.flatMap((item) => collectRegisterMessages(item, field));
    }

    if (typeof data === 'object') {
        return Object.entries(data).flatMap(([key, value]) => (
            collectRegisterMessages(value, key)
        ));
    }

    return [];
};

const getRegisterErrorMessage = (error) => {
    const messages = collectRegisterMessages(error.response?.data)
        .filter(Boolean);

    return messages.length > 0
        ? messages.join('\n')
        : 'No se pudo crear la cuenta. Inténtalo de nuevo.';
};

export function useAuthContext() {
    return useContext(authContext);
}
export function useAuthToggleContext() {
    return useContext(authToggleContext);
}

// eslint-disable-next-line react/prop-types
const AuthProvider = ({ children }) => {
    const navigate = useNavigate();

    // Estado inicial de autenticación
    const initialStateAuth = {
        isAuth: false,
        role: null,
        // Permisos Django reales del usuario (p. ej. `store.manage_shipping_config`),
        // no solo `role`. Sirve para ocultar secciones administrativas concretas a
        // quien no las tiene; el backend vuelve a comprobar el permiso en cada
        // endpoint, así que ocultar el enlace nunca es la única defensa.
        permissions: [],
        isLoading: true,
    };
    const [isAuth, setIsAuth] = useState(initialStateAuth);
    const isRefreshing = useRef(false);
    const refreshSubscribers = useRef([]);

    const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;
    axios.defaults.baseURL = API_BASE_URL;
    axios.defaults.withCredentials = true;
    const clearStoredSession = () => {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        delete axios.defaults.headers.common["Authorization"];
    };

    const storeSessionTokens = (tokens) => {
        localStorage.setItem("access_token", tokens.access);
        if (tokens.refresh) {
            localStorage.setItem("refresh_token", tokens.refresh);
        }
        axios.defaults.headers.common["Authorization"] = `Bearer ${tokens.access}`;
    };

    const checkAuthStatus = async () => {
        let accessToken = localStorage.getItem("access_token");
        const refreshToken = localStorage.getItem("refresh_token");

        if (!accessToken && !refreshToken) {
            setIsAuth({ isAuth: false, role: null, permissions: [], isLoading: false });
            return;
        }

        try {
            if (!accessToken) {
                const refreshResponse = await axios.post("/token/refresh/", { refresh: refreshToken });
                storeSessionTokens(refreshResponse.data);
                accessToken = refreshResponse.data.access;
            }
            axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
            let response;
            try {
                response = await axios.post("/verify-token/", { token: accessToken });
            } catch (error) {
                if (error.response?.status !== 401 || !refreshToken) {
                    throw error;
                }
                const refreshResponse = await axios.post("/token/refresh/", { refresh: refreshToken });
                storeSessionTokens(refreshResponse.data);
                accessToken = refreshResponse.data.access;
                response = await axios.post("/verify-token/", { token: accessToken });
            }

            const userRole = response.data.role;
            console.log("Usuario autenticado:", response.data);
            setIsAuth({
                isAuth: true, role: userRole,
                permissions: response.data.permissions || [],
                isLoading: false,
            });
        } catch (error) {
            console.error("Error al verificar el token:", error);
            clearStoredSession();
            setIsAuth({ isAuth: false, role: null, permissions: [], isLoading: false });
        }
    };

    const theme = getThemeContext();
    // Logout
    const handleLogout = () => {
        const refreshToken = localStorage.getItem("refresh_token");
        clearStoredSession();
        // Navegar a home de forma síncrona antes de actualizar el estado de auth,
        // para evitar que RequireAuth redirija a /login mientras aún estamos en una ruta protegida.
        flushSync(() => navigate("/"));
        setIsAuth({ isAuth: false, role: null, permissions: [], isLoading: false });
        showAlert({
            title: "<p>Desconexión satisfactoria</p>",
            icon: 'success',
            theme: theme
        });
        if (refreshToken) {
            axios.post("/logout/", { refresh_token: refreshToken }).catch(() => {});
        }
    };
    const { fetchCartItemsLogued } = useCartContext();
    // Login
    const handleLogin = async (user, redirectTo = null) => {
        try {
            const response = await axios.post("/token/", user, {
                headers: { "Content-Type": "application/json" },
            });

            storeSessionTokens(response.data);

            const tokenPayload = JSON.parse(atob(response.data.access.split(".")[1]));
            const userRole = tokenPayload.role;

            try {
                const mergeResponse = await axios.post("/cart/merge/", null, {
                    withCredentials: true,
                });
                console.log("Carrito fusionado exitosamente:", mergeResponse.data);

                //Actualizar el carrito en el contexto
                // const fetchCartItemsLogued =
                // fetchCartItemsLogued();
            } catch (mergeError) {
                console.error("Error al fusionar carritos:", mergeError);
            } finally {
                fetchCartItemsLogued();
            }

            // `permissions` llega vacío aquí (el JWT solo trae `role`); el
            // `checkAuthStatus()` de abajo lo rellena en cuanto responde
            // `/verify-token/`, que sí consulta los permisos reales del usuario.
            setIsAuth({ isAuth: true, role: userRole, permissions: [], isLoading: false });
            const safeRedirect = redirectTo?.startsWith("/") && !redirectTo.startsWith("//")
                ? redirectTo
                : null;
            navigate(safeRedirect || (userRole === "admin" ? "/admin/general" : userRole === "comercial" ? "/admin/proyectos" : "/profile/general/"), { replace: true });

            checkAuthStatus()
        } catch (error) {
            console.error("Error al iniciar sesión:", error);
            showAlert({
                title: "<p>Usuario o contraseña incorrectos</p>",
                icon: "error",
                theme: theme,
            });
        }
    };

    // Register
    const handleRegister = async ({ name, email, password, password2 }) => {
        if (password !== password2) {
            showAlert({ title: "<p>Las contraseñas no coinciden</p>", icon: "error", theme });
            return false;
        }
        try {
            await axios.post("/register/", { name, email, password }, {
                headers: { "Content-Type": "application/json" },
            });
            return true; // señal de éxito: la página mostrará el mensaje de "verifica tu email"
        } catch (error) {
            console.error("Error al registrarse:", error);
            showAlert({
                title: "No se pudo crear la cuenta",
                text: getRegisterErrorMessage(error),
                icon: "error",
                theme,
            });
            return false;
        }
    };

    // Forgot password
    const handleForgotPassword = async (email) => {
        try {
            await axios.post("/auth/password-reset/", { email }, {
                headers: { "Content-Type": "application/json" },
            });
            return true;
        } catch {
            return true; // siempre true para no revelar si el email existe
        }
    };

    // Reset password confirm
    const handleResetPassword = async ({ uid, token, password }) => {
        try {
            await axios.post("/auth/password-reset/confirm/", { uid, token, password }, {
                headers: { "Content-Type": "application/json" },
            });
            return { ok: true };
        } catch (error) {
            const msg = error.response?.data?.detail
                || error.response?.data?.password?.[0]
                || "No se pudo restablecer la contraseña. El enlace puede haber expirado.";
            return { ok: false, message: msg };
        }
    };

    // Verify email
    const handleVerifyEmail = async ({ uid, token }) => {
        try {
            await axios.post("/auth/verify-email/", { uid, token }, {
                headers: { "Content-Type": "application/json" },
            });
            return { ok: true };
        } catch (error) {
            const msg = error.response?.data?.detail || "Enlace inválido o expirado.";
            return { ok: false, message: msg };
        }
    };









// Interceptor de respuesta
    useEffect(() => {
        const onRefreshed = (newToken) => {
            refreshSubscribers.current.forEach((callback) => callback(newToken));
            refreshSubscribers.current = [];
        };

        const addSubscriber = (callback) => {
            refreshSubscribers.current.push(callback);
        };

        const interceptorId = axios.interceptors.response.use(
            (response) => response, // Si la respuesta es exitosa, la devolvemos
            async (error) => {
                const originalRequest = error.config;
                const status = error.response?.status;

                if (!originalRequest || !status) {
                    return Promise.reject(error);
                }

                // Si el error es 401, excluir las URLs de auth para no entrar en bucle
                if (
                    status === 401 &&
                    !originalRequest._retry &&
                    originalRequest.url !== "/token/refresh/" &&
                    originalRequest.url !== "/token/" &&
                    originalRequest.url !== "/verify-token/" &&
                    originalRequest.url !== "/logout/"
                ) {
                    originalRequest._retry = true;

                    if (!isRefreshing.current) {
                        isRefreshing.current = true;
                        const refreshToken = localStorage.getItem("refresh_token");
                        axios.post("/token/refresh/", { refresh: refreshToken })
                            .then((response) => {
                                const newAccessToken = response.data.access;
                                storeSessionTokens(response.data);
                                isRefreshing.current = false;
                                onRefreshed(newAccessToken);
                            })
                            .catch((err) => {
                                isRefreshing.current = false;
                                console.error("Error al renovar el token:", err);
                                clearStoredSession();
                                setIsAuth({ isAuth: false, role: null, permissions: [], isLoading: false });
                                // Sin redirigir a /login: una sesión caducada en
                                // una página pública (el visor por QR, la tienda)
                                // mandaba al login por el 401 del carrito. Las
                                // peticiones se repiten como anónimas y las rutas
                                // protegidas ya redirigen solas con RequireAuth.
                                onRefreshed(null);
                            });
                    }

                    // El subscriber se añade siempre, antes de que el refresh complete
                    return new Promise((resolve) => {
                        addSubscriber((newToken) => {
                            originalRequest.headers = originalRequest.headers || {};
                            if (newToken) {
                                originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
                            } else {
                                delete originalRequest.headers["Authorization"];
                            }
                            resolve(axios(originalRequest));
                        });
                    });
                }

                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.response.eject(interceptorId);
        };
    }, []);

    // Verificar si el usuario está autenticado al montar el componente
    useEffect(() => {
        checkAuthStatus();
    }, []);











    return (
        <authContext.Provider value={isAuth}>
            <authToggleContext.Provider value={{ handleLogin, handleLogout, handleRegister, handleForgotPassword, handleResetPassword, handleVerifyEmail }}>
                {children}
            </authToggleContext.Provider>
        </authContext.Provider>
    );




};

export default AuthProvider;
